import React from 'react';
import { Phone, MapPin, GraduationCap, Sparkles, Award, Target, Clock, ArrowRight, BookOpen } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleGoToDocuments = () => {
    navigate('/documents');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleGoToFormations = () => {
    const scrollToFormations = () => {
      const element = document.getElementById('formations');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(scrollToFormations, 200);
    } else {
      scrollToFormations();
    }
  };

  return (
    <footer className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white relative overflow-hidden">
      {/* Motif de fond */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-20 w-32 h-32 border border-white/20 rounded-full" />
        <div className="absolute top-40 right-20 w-24 h-24 border border-white/20 rounded-full" />
        <div className="absolute bottom-20 left-1/3 w-16 h-16 border border-white/20 rounded-full" />
        <div className="absolute bottom-40 right-1/3 w-20 h-20 border border-white/20 rounded-full" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Section CTA */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-10 sm:mb-16"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-6">
            Prêt à rejoindre l'excellence scientifique ?
          </h2>
          <p className="text-base sm:text-lg text-white/80 mb-6 sm:mb-8 max-w-xl sm:max-w-2xl mx-auto">
            Découvrez nos ressources pédagogiques et commencez votre parcours vers l'excellence.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
            <motion.button
              onClick={handleGoToDocuments}
              className="group bg-white text-blue-600 px-6 sm:px-8 py-3 sm:py-4 rounded-lg sm:rounded-xl font-semibold text-base sm:text-lg transition-all duration-300 shadow-2xl hover:shadow-white/25 flex items-center space-x-2 sm:space-x-3"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <span>Accéder aux documents</span>
              <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
            </motion.button>
            
            <motion.button
              onClick={handleGoToFormations}
              className="group bg-white/10 backdrop-blur-md border border-white/20 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg sm:rounded-xl font-semibold text-base sm:text-lg transition-all duration-300 hover:bg-white/20 flex items-center space-x-2 sm:space-x-3"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <span>Découvrir nos formations</span>
              <BookOpen className="h-4 w-4 sm:h-5 sm:w-5 group-hover:rotate-12 transition-transform" />
            </motion.button>

            <motion.a
              href="https://maps.app.goo.gl/iboaayWNsjRq1poh9?g_st=iwb"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white/10 backdrop-blur-md border border-white/20 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg sm:rounded-xl font-semibold text-base sm:text-lg transition-all duration-300 hover:bg-white/20 flex items-center space-x-2 sm:space-x-3"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <span>Nous localiser</span>
              <MapPin className="h-4 w-4 sm:h-5 sm:w-5" />
            </motion.a>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Logo et Description */}
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <GraduationCap className="h-7 w-7 text-white" />
                </div>
                <motion.div
                  className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Sparkles className="h-2 w-2 text-white" />
                </motion.div>
              </div>
              <div>
                <span className="text-2xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                  LES CRAC
                </span>
                <span className="block text-sm font-medium text-white/60">
                  ACADEMY
                </span>
              </div>
            </div>
            <p className="text-white/80 leading-relaxed text-lg">
              Un Lycée d'excellence résolument tourné vers l'avenir scientifique...
            </p>
            <div className="flex items-center space-x-4">
              <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
                <span className="text-white/90 font-medium text-sm">Excellence Académique</span>
              </div>
            </div>
          </div>

          {/* Contact avec design moderne */}
          <div className="space-y-6">
            <h3 className="text-xl font-bold flex items-center space-x-2">
              <Award className="h-6 w-6 text-blue-300" />
              <span>Contact</span>
            </h3>
            <div className="space-y-4">
              <motion.div 
                className="flex items-center space-x-4 group"
                whileHover={{ x: 5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 w-10 h-10 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <Phone className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-white/60 text-sm">Téléphone</p>
                  <p className="text-white font-semibold">+221 33 894 37 52</p>
                </div>
              </motion.div>
              
              <motion.div 
                className="flex items-center space-x-4 group"
                whileHover={{ x: 5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="bg-gradient-to-br from-green-500 to-green-600 w-10 h-10 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <MapPin className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-white/60 text-sm">Adresse</p>
                  <p className="text-white font-semibold">Cité Alioune SOW de Golf</p>
                  <a
                    href="https://maps.app.goo.gl/iboaayWNsjRq1poh9?g_st=iwb"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/80 underline text-xs hover:text-white"
                  >
                    Voir sur la carte
                  </a>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Informations avec design moderne */}
          <div className="space-y-6">
            <h3 className="text-xl font-bold flex items-center space-x-2">
              <Target className="h-6 w-6 text-blue-300" />
              <span>Informations</span>
            </h3>
            <div className="space-y-4">
              <motion.div 
                className="flex items-center space-x-3 group"
                whileHover={{ x: 5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                <span className="text-white/80">Admission sur test d'entrée</span>
              </motion.div>
              
              <motion.div 
                className="flex items-center space-x-3 group"
                whileHover={{ x: 5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span className="text-white/80">Classes d'élite par niveau</span>
              </motion.div>
              
              <motion.div 
                className="flex items-center space-x-3 group"
                whileHover={{ x: 5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                <span className="text-white/80">Formation scientifique d'excellence</span>
              </motion.div>
              
              <motion.div 
                className="flex items-center space-x-3 group"
                whileHover={{ x: 5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                <span className="text-white/80">Suivi personnalisé</span>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Séparateur décoratif */}
        <motion.div 
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent my-12"
        />

        {/* Copyright avec design moderne */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="text-center"
        >
          <div className="flex items-center justify-center space-x-4 mb-4">
            <Clock className="h-5 w-5 text-white/60" />
            <span className="text-white/60 text-sm">Année Académique 2024/2025</span>
          </div>
          <p className="text-white/60 font-medium">
            © 2024 LES CRAC ACADEMY. Tous droits réservés.
          </p>
          <p className="text-white/40 text-sm mt-2">
            Formation d'excellence pour l'avenir scientifique
          </p>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;